import React, {ReactNode} from 'react';

import ListPage from 'shared/ui/ListPage/ListPage';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';
import PlugNoAccess from 'shared/ui/Plugs/PlugNoAccess/PlugNoAccess';
import PlugNoIncidents from 'shared/ui/Plugs/PlugNoIncidents/PlugNoIncidents';
import {ITableProps} from 'shared/ui/Table/Table';
import useOpenState from 'shared/lib/hooks/useOpenState';
import {PlugState} from 'entities/user';

import {getIncidentsEmptyState} from '../../lib/incidentsScreen';

import IncidentsSummary, {IIncidentsSummaryProps} from '../IncidentsSummary/IncidentsSummary';

export interface IIncidentsModalState {
    open: boolean,
    handleClose: () => void,
}

interface IIncidentsScreenProps<T extends object> extends Omit<ITableProps<T>, 'className' | 'emptyStateComponent'> {
    documentTitle: string,
    title: string,
    backUrl?: string,
    isFetching: boolean,
    refetch: () => void,
    summary: IIncidentsSummaryProps,
    onSubmitSearch?: (_search: string) => void,
    searchValue?: string,
    renderFilterModal?: (_state: IIncidentsModalState) => ReactNode,
    renderExportModal?: (_state: IIncidentsModalState) => ReactNode,
    plugState: PlugState,
    createButton: ReactNode,
    createText: string,
    onCreate: () => void,
    children?: ReactNode,
}

function IncidentsScreen<T extends object>({
    documentTitle,
    title,
    backUrl,
    isFetching,
    refetch,
    summary,
    onSubmitSearch,
    searchValue,
    renderFilterModal,
    renderExportModal,
    plugState,
    createButton,
    createText,
    onCreate,
    children,
    ...tableProps
}: IIncidentsScreenProps<T>) {
    const filter = useOpenState();
    const exportModal = useOpenState();

    const handleRefresh = () => {
        refetch();
        summary.refetch?.();
    };

    return (
        <ListPage
            {...tableProps}
            documentTitle={documentTitle}
            toolbar={
                <ListToolbar
                    title={title}
                    backUrl={backUrl}
                    summary={<IncidentsSummary {...summary}/>}
                    createButton={createButton}
                    hideCreateButtonOnMobile={plugState === 'noAccess' || plugState === 'empty'}
                    actions={
                        <TableToolbar
                            onSearch={onSubmitSearch}
                            searchValue={searchValue}
                            onFilter={renderFilterModal && filter.open}
                            onExport={renderExportModal && exportModal.open}
                            onRefresh={handleRefresh}
                            isRefreshing={isFetching}
                        />
                    }
                />
            }
            emptyStateComponent={getIncidentsEmptyState(plugState)}
            plug={
                <>
                    {plugState === 'noAccess' && <PlugNoAccess/>}
                    {plugState === 'empty' && <PlugNoIncidents onCreate={onCreate} buttonText={createText}/>}
                </>
            }
        >
            {renderFilterModal?.({open: filter.isOpen, handleClose: filter.close})}
            {renderExportModal?.({open: exportModal.isOpen, handleClose: exportModal.close})}
            {children}
        </ListPage>
    );
}

export default IncidentsScreen;
